import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import Modal from 'src/components/layouts/Modal/Modal';
import Input from 'src/components/ui/Input/Input';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import Select, { Option } from 'src/components/ui/Select/Select';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Tooltip from 'src/components/ui/Tooltip/Tooltip';
import {
  CustomerForecastPromotion,
  CustomerForecastPromotionWriteIn,
  PROMOTION_TYPE_LABEL_KEY,
  PromotionType,
  useCreatePromotion,
  useOwnPromotions,
  useRequestPromotionChange,
  useUpdatePromotion,
} from 'src/services/forecastPromotions';
import { useProfile } from 'src/utils/ProfileService';

const TYPES: PromotionType[] = ['black_week', 'sale', 'launch', 'campaign', 'season', 'other'];

interface Props {
  mode: 'create' | 'edit' | 'request-edit';
  initial?: CustomerForecastPromotion;
  onClose: () => void;
}

function todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function PromotionPeriodModal({ mode, initial, onClose }: Props) {
  const { t } = useTranslation();
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const requestChange = useRequestPromotionChange();
  const { data: allPromos } = useOwnPromotions();
  const isRequest = mode === 'request-edit';

  const initialUseAdditional = !!(initial?.additional_orders && initial.additional_orders > 0);

  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<PromotionType>(initial?.type ?? 'other');
  const [dateFrom, setDateFrom] = useState(initial?.date_from ?? todayPlus(0));
  const [dateTo, setDateTo] = useState(initial?.date_to ?? todayPlus(30));
  const [useAdditional, setUseAdditional] = useState(initialUseAdditional);
  const [multiplier, setMultiplier] = useState<string>(initial?.multiplier ? String(initial.multiplier) : '');
  const [additionalOrders, setAdditionalOrders] = useState<string>(
    initial?.additional_orders ? String(initial.additional_orders) : '',
  );
  const [description, setDescription] = useState(initial?.description ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [seedTick, setSeedTick] = useState(0);
  const [toTick, setToTick] = useState(0);

  const occupied = (allPromos ?? []).filter(p => p.id !== initial?.id && p.state !== 'rejected');

  const isDayOccupied = (day: Dayjs) => {
    const d = day.format('YYYY-MM-DD');
    return occupied.some(p => d >= p.date_from && d <= p.date_to);
  };

  // A promotion's start may not lie in the past; there is no upper bound. An
  // existing promotion whose start is already in the past is grandfathered so
  // it stays editable.
  const today = dayjs().startOf('day');
  const initialFrom = initial?.date_from ? dayjs(initial.date_from) : null;
  const fromMinDate = initialFrom && initialFrom.isBefore(today) ? initialFrom : today;
  const toMinDate: Dayjs = dateFrom ? dayjs(dateFrom) : fromMinDate;

  const { profile, loading: profileLoading } = useProfile();
  const lang = profileLoading ? null : profile.lang;
  const decimalSep = (() => {
    if (!lang) return '.';
    const bcp47 = lang.includes('_') ? lang.replace('_', '-') : lang;
    return new Intl.NumberFormat(bcp47).formatToParts(1.1).find(p => p.type === 'decimal')?.value ?? '.';
  })();
  const displayMultiplier = multiplier === '' ? '' : multiplier.replace('.', decimalSep);
  const handleMultiplierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = e.target.value.replace(',', '.');
    if (normalized === '' || /^\d*\.?\d*$/.test(normalized)) {
      setMultiplier(normalized);
    }
  };

  // On create, seed the dates to the first free day from today onwards (today
  // itself, or the window's start day, may already be taken by another
  // promotion). Editing existing promotions keeps their stored dates.
  const didSeedDates = useRef(false);
  useEffect(() => {
    if (mode !== 'create' || didSeedDates.current || allPromos === undefined) return;
    const taken = allPromos.filter(p => p.state !== 'rejected');
    const isTaken = (ds: string) => taken.some(p => ds >= p.date_from && ds <= p.date_to);
    let day = dayjs().startOf('day');
    const end = day.add(365, 'day');
    let free = day.format('YYYY-MM-DD');
    while (!day.isAfter(end)) {
      const ds = day.format('YYYY-MM-DD');
      if (!isTaken(ds)) {
        free = ds;
        break;
      }
      day = day.add(1, 'day');
    }
    setDateFrom(free);
    setDateTo(free);
    didSeedDates.current = true;
    setSeedTick(tick => tick + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, allPromos]);

  const busy = create.isPending || update.isPending || requestChange.isPending;

  const typeOptions: Option[] = useMemo(
    () => TYPES.map(tp => ({ value: tp, label: t(PROMOTION_TYPE_LABEL_KEY[tp]) })),
    [t],
  );

  let modalTitle = t('forecastPromotions.newPromo');
  if (isRequest) modalTitle = t('forecastPromotions.requestEditTitle');
  else if (mode === 'edit') modalTitle = t('forecastPromotions.editPromo');

  let submitLabel = t('forecastPromotions.save');
  if (busy) submitLabel = t('forecastPromotions.saving');
  else if (isRequest) submitLabel = t('forecastPromotions.requestChangeSubmit');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('forecastPromotions.errors.nameRequired'));
      return;
    }
    if (!dateFrom || !dateTo || dateTo < dateFrom) {
      setError(t('forecastPromotions.errors.dateRange'));
      return;
    }
    if (dayjs(dateFrom).isBefore(fromMinDate)) {
      setError(t('forecastPromotions.errors.dateInPast'));
      return;
    }
    const clash = occupied.find(p => dateFrom <= p.date_to && dateTo >= p.date_from);
    if (clash) {
      setError(
        t('forecastPromotions.errors.overlap', {
          start: dayjs(clash.date_from).format('DD.MM.YYYY'),
          end: dayjs(clash.date_to).format('DD.MM.YYYY'),
        }),
      );
      return;
    }
    const mult = useAdditional ? null : Number(multiplier);
    const add = useAdditional ? Number(additionalOrders) : null;
    if (!useAdditional && (!mult || mult < 1)) {
      setError(t('forecastPromotions.errors.multiplierMin'));
      return;
    }
    if (useAdditional && (add === null || Number.isNaN(add) || add < 0)) {
      setError(t('forecastPromotions.errors.additionalMin'));
      return;
    }

    const payload: CustomerForecastPromotionWriteIn = {
      name: name.trim(),
      type,
      date_from: dateFrom,
      date_to: dateTo,
      multiplier: mult,
      additional_orders: add,
      description: description.trim() || null,
    };

    try {
      if (isRequest && initial) {
        await requestChange.mutateAsync({
          id: initial.id,
          payload: { request_type: 'edit', reason: reason.trim() || null, changes: payload },
        });
      } else if (mode === 'edit' && initial) {
        await update.mutateAsync({ id: initial.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal isOpen onClose={onClose} smallSize>
      <form onSubmit={onSubmit} className="max-w-[480px] w-full">
        <div className="p-32 flex flex-col gap-16">
          <h4 className="typo-title-24 text-full">{modalTitle}</h4>
          {isRequest && <p className="typo-text-14 text-faded">{t('forecastPromotions.requestEditDescription')}</p>}

          <Input
            label={t('forecastPromotions.titleField')}
            placeholder=""
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full"
          />

          <Select
            options={typeOptions}
            defaultValue={typeOptions.find(o => o.value === type)}
            onChange={opt => setType(opt.value as PromotionType)}
            fullWidth
          />

          <div className="flex gap-12">
            <div className="flex-1">
              <DatePicker
                key={`from-${seedTick}`}
                label={t('forecastPromotions.col.from')}
                defaultValue={dateFrom ? dayjs(dateFrom) : null}
                onChange={val => {
                  const next = val ? dayjs(val).format('YYYY-MM-DD') : '';
                  setDateFrom(next);
                  if (next && dateTo && next > dateTo) {
                    setDateTo(dayjs(next).add(1, 'day').format('YYYY-MM-DD'));
                    setToTick(tick => tick + 1);
                  }
                }}
                minDate={fromMinDate}
                shouldDisableDate={isDayOccupied}
              />
            </div>
            <div className="flex-1">
              <DatePicker
                key={`to-${seedTick}-${toTick}`}
                label={t('forecastPromotions.col.to')}
                defaultValue={dateTo ? dayjs(dateTo) : null}
                onChange={val => setDateTo(val ? dayjs(val).format('YYYY-MM-DD') : '')}
                minDate={toMinDate}
                shouldDisableDate={isDayOccupied}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 surface-elevated-darken rounded-base px-4 py-2">
            <button
              type="button"
              onClick={() => setUseAdditional(false)}
              className={`flex-1 py-8 px-8 rounded-base typo-text-14 inline-flex items-center justify-center gap-4 ${
                !useAdditional ? 'surface-elevated typo-text-14--bold text-full shadow-sm' : 'text-faded'
              }`}>
              {t('forecastPromotions.multiplier')}
              <Tooltip title={t('forecastPromotions.multiplierHelp')} />
            </button>
            <button
              type="button"
              onClick={() => setUseAdditional(true)}
              className={`flex-1 py-8 px-8 rounded-base typo-text-14 inline-flex items-center justify-center gap-4 ${
                useAdditional ? 'surface-elevated typo-text-14--bold text-full shadow-sm' : 'text-faded'
              }`}>
              {t('forecastPromotions.additionalOrders')}
              <Tooltip title={t('forecastPromotions.additionalOrdersHelp')} />
            </button>
          </div>

          {!useAdditional ? (
            <Input
              type="text"
              label={t('forecastPromotions.multiplier')}
              placeholder={t('forecastPromotions.multiplierPlaceholder')}
              value={displayMultiplier}
              onChange={handleMultiplierChange}
              className="w-full"
            />
          ) : (
            <Input
              type="number"
              min={0}
              label={t('forecastPromotions.additionalOrders')}
              placeholder={t('forecastPromotions.additionalOrdersPlaceholder')}
              value={additionalOrders}
              onChange={e => setAdditionalOrders(e.target.value)}
              className="w-full"
            />
          )}

          <Textarea
            label={t('general.description')}
            placeholder=""
            value={description}
            onChange={e => setDescription(e.target.value)}
            minRows={3}
          />

          {isRequest && (
            <Textarea
              label={t('forecastPromotions.reasonLabel')}
              placeholder={t('forecastPromotions.reasonPlaceholder')}
              value={reason}
              onChange={e => setReason(e.target.value)}
              minRows={2}
            />
          )}

          {error && <span className="typo-text-12 status-error-full">{error}</span>}
        </div>

        <div className="border-subtle-top py-16 px-24 flex justify-end gap-12">
          <Button
            tone={ButtonToneProps.SECONDARY}
            title={t('forecastPromotions.cancel')}
            onClick={onClose}
            disabled={busy}
          />
          <Button tone={ButtonToneProps.PRIMARY_BLUE} title={submitLabel} type="submit" disabled={busy} />
        </div>
      </form>
    </Modal>
  );
}
